import React, { useState } from 'react';
import request from 'utils/request_ajax';
import { Icon, Spinner } from 'ui';
import ModalContainer from 'components/shared/modal/modal_container';
import RenderInBody from 'components/shared/render_in_body';
import { routes } from 'constants/config';
import useIsJointUser from 'components/shared/hooks/useIsJointUser';
import {
  MODAL_TYPES,
  MODAL_COMPONENTS,
  LESSON_CONTENT_TYPES,
  LESSON_TYPES,
} from './constants';
import LessonGenerateActions from './LessonGenerateActions';
import ModalHeader from './ModalHeader';

const LessonGenerateModal = ({
  lessonTitle,
  lessonDescription,
  workspaceId,
  closeModal,
  openLessonCreateModal,
}) => {
  const [modal, setModal] = useState(MODAL_TYPES.INITIAL_INFORMATION);
  const [lessonText, setLessonText] = useState('');
  const [formatChoice, setFormatChoice] = useState(true);
  const [lessonType, setLessonType] = useState(null);
  const [topic, setTopic] = useState('');
  const [details, setDetails] = useState('');
  const [lessonContent, setLessonContent] = useState(null);
  const [contentVersionId, setContentVersionId] = useState(null);
  const { isJointUser, loading } = useIsJointUser();

  const previous = () => {
    if (modal === MODAL_TYPES.INITIAL_INFORMATION) {
      openLessonCreateModal();
    } else {
      const { previous: previousModal } = MODAL_COMPONENTS[modal];
      setModal(
        typeof previousModal === 'function'
          ? previousModal(
              lessonType,
              lessonContent,
              contentVersionId,
              setContentVersionId,
              isJointUser,
            )
          : previousModal,
      );
    }
  };

  const nextModal = () => {
    const { next } = MODAL_COMPONENTS[modal];
    setModal(
      typeof next === 'function'
        ? next(
            lessonType,
            lessonContent,
            contentVersionId,
            setContentVersionId,
            isJointUser,
          )
        : next,
    );
  };

  const sendRequest = () => {
    const requestParams = {};
    let endpoint;
    if (lessonTitle) requestParams.title = lessonTitle;
    if (lessonDescription) requestParams.description = lessonDescription;
    if (workspaceId) requestParams.workspace_id = workspaceId;

    requestParams.gpt_formatted = formatChoice;

    if (lessonType === LESSON_TYPES.TOPIC) {
      endpoint = routes.LESSON_GENERATE.TOPIC;
      requestParams.topic = topic;
      if (details) requestParams.additional_information = details;
    } else if (lessonContent === LESSON_CONTENT_TYPES.SEISMIC) {
      endpoint = routes.LESSON_GENERATE.SEISMIC;
      requestParams.content_version_id = contentVersionId;
    } else {
      endpoint = routes.LESSON_GENERATE.TEXT;
      requestParams.raw_input = lessonText;
    }
    nextModal();
    request
      .postJSON(endpoint, requestParams)
      .done(resp => {
        if (resp.success) {
          request.getScript(routes.NOTIFICATIONS_LATEST);
        }
      })
      .fail(error => {
        console.error(error);
      });
  };

  const routeToContent = () => {
    if (modal === MODAL_TYPES.FINISH) {
      if (window.location.pathname !== routes.CONTENT.INDEX) {
        window.location.assign(routes.CONTENT.INDEX);
      }
      closeModal();
    }
  };

  const next = () => {
    routeToContent();
    if (modal === MODAL_TYPES.FORMATTING) {
      sendRequest();
    } else {
      nextModal();
    }
  };

  const close = () => {
    if (modal !== MODAL_TYPES.FINISH) {
      closeModal();
    } else {
      routeToContent();
    }
  };

  const isNextBtnDisabled =
    (modal === MODAL_TYPES.ADD_TEXT_SPACE_BELOW && !lessonText.trim().length) ||
    (modal === MODAL_TYPES.INITIAL_INFORMATION && !lessonType) ||
    (modal === MODAL_TYPES.INITIAL_INFORMATION &&
      !isJointUser &&
      !lessonType) ||
    (modal === MODAL_TYPES.INITIAL_INFORMATION &&
      lessonType === LESSON_TYPES.CONTENT &&
      !lessonContent &&
      isJointUser) ||
    (modal === MODAL_TYPES.TOPIC_DETAILS && !topic) ||
    (modal === MODAL_TYPES.CONTENT_LESSON && !contentVersionId);

  const documentPicked = items => {
    let selectedVersionId = null;
    if (items.length === 1) {
      const {
        libraryContent: { versionId },
      } = items[0];
      selectedVersionId = versionId;
    }
    setContentVersionId(selectedVersionId);
  };

  const { ModalBody, title, subheader } = MODAL_COMPONENTS[modal];

  const Loader = () => {
    return (
      <div className="SeismicContent--Spinner">
        <Spinner size="larger" />
      </div>
    );
  };

  return (
    <RenderInBody>
      <ModalContainer
        className="ModalTakeover-background Modal-background d-f ai-c"
        onEscape={close}
        manualAutoFocus
      >
        <div className="ModalTakeover-container LessonGenerateModal">
          <div className="LessonGenerateModal--body">
            {loading ? (
              <Loader />
            ) : (
              <>
                <ModalHeader title={title} subheader={subheader} />
                <ModalBody
                  modal={modal}
                  lessonText={lessonText}
                  formatChoice={formatChoice}
                  setFormatChoice={setFormatChoice}
                  lessonType={lessonType}
                  setLessonType={setLessonType}
                  topic={topic}
                  setTopic={setTopic}
                  details={details}
                  setDetails={setDetails}
                  lessonContent={lessonContent}
                  setLessonContent={setLessonContent}
                  setLessonText={setLessonText}
                  documentPicked={documentPicked}
                  isJointUser={isJointUser}
                />
                <LessonGenerateActions
                  previous={previous}
                  next={next}
                  modal={modal}
                  isNextBtnDisabled={isNextBtnDisabled}
                  displayWordCount={modal === MODAL_TYPES.ADD_TEXT_SPACE_BELOW}
                  lessonText={lessonText}
                />
              </>
            )}

            <button
              type="button"
              className="u-resetBtn ModalTakeover-closeButton"
              aria-label={t('common.close')}
              onClick={close}
            >
              <Icon name="error" />
            </button>
          </div>
        </div>
      </ModalContainer>
    </RenderInBody>
  );
};

export default LessonGenerateModal;
